import { X } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldComplex } from 'src/components/Form/hook-form/field-complex';
import { OptionObject, ReactSelectHookFormInput } from 'src/components/ReactSelectInput';
import { Button } from 'src/components/ui/button';
import { createFilter } from 'react-select';
import { cn } from 'src/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { authQueries, useCompanyQuery } from 'src/api/queries';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { object, string } from 'yup';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { ButtonLoader } from 'src/components/ui/loader';
import { ChangeEditorRequest, requestClient } from 'src/lib/services/api/request-api';
import { EditorSelectOption } from 'src/components/ui/react-select/select-options/editor-select-option';
import { useToast } from 'src/components/ui/use-toast';

type Props = {
  companyId?: string;
  requestId: string;
  editorId: string;
  onSuccess?: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
};

const RequestEditorChangeDialog: React.FC<Props> = ({
  companyId,
  requestId,
  editorId,
  onSuccess,
  open,
  setOpen,
  toggle,
}) => {
  const { t } = useTranslation('pages/shared');
  const { toast } = useToast();
  const client = useQueryClient();
  const { data } = useCompanyQuery(companyId!);

  const editorsOptions = (data?.subscriptions || []).flatMap((sub) => {
    return (sub.assignments || []).map((assignment) => {
      if (!assignment.editor) {
        return;
      }

      const fullName = joinExistingValues(
        [assignment.editor?.first_name, assignment.editor?.last_name],
        ' ',
      );

      return {
        label: fullName,
        value: assignment.editor.id,
        avatar: assignment.editor.avatar,
      };
    });
  }) as OptionObject[];

  const currentEditor = editorsOptions.find((editor) => editor.value === editorId);

  const editorValidationScheme = useMemo(
    () =>
      object({
        editor_id: string().required(),
      }),
    [],
  );

  const form = useForm<ChangeEditorRequest>({
    defaultValues: {
      editor_id: currentEditor?.value,
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(editorValidationScheme),
  });

  const onSubmit: SubmitHandler<ChangeEditorRequest> = async (values) => {
    if (editorId === values.editor_id) {
      form.setError('editor_id', {
        type: 'custom',
        message: 'This editor is already assigned to this request.',
      });
      return;
    }

    try {
      await requestClient.changeEditor(requestId, { editor_id: values.editor_id });
      // The editor change cascades to every outcome in the project (#2554).
      // Outcome lists are cached under the project id; this dialog is always
      // opened with the parent request id, so drop that project's cached
      // outcomes — otherwise siblings keep showing the previous editor until a
      // manual refetch. Harmless for non-multi-outcome requests (nothing cached).
      client.invalidateQueries({ queryKey: [authQueries.requestOutcomes, requestId] });
      toast({ description: t('Editor changed successfully') });
      toggle();
      onSuccess?.();
    } catch (error) {
      // Global Http interceptor already toasts the backend message (or a
      // friendly fallback for network/server errors); no local toast needed.
      console.error('Failed to change editor', error);
    }
  };

  return (
    <Dialog
      modal
      open={open}
      onOpenChange={(open) => {
        form.reset({
          editor_id: currentEditor?.value,
        });
        setOpen(open);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className={'tw-text-2xl tw-font-bold tw-tracking-tight '}>
            {t('requests_list.change_editor', {
              defaultValue: 'Change Editor',
            })}
          </DialogTitle>
          <DialogClose asChild>
            <Button type={'button'} variant={'ghost'} size={'icon'}>
              <X size={20} />
            </Button>
          </DialogClose>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={'tw-py-4'}>
              <FieldComplex
                name={'editor_id'}
                label={t('requests_list.editor', {
                  defaultValue: 'Editor',
                })}
              >
                <ReactSelectHookFormInput
                  name={'editor_id'}
                  isClearable={false}
                  isSearchable
                  filterOption={createFilter({ ignoreAccents: false })}
                  options={editorsOptions}
                  className={cn({
                    'validation-error tw-border-destructive': form.formState.errors.editor_id,
                  })}
                  components={{
                    Option: EditorSelectOption,
                  }}
                />
              </FieldComplex>
            </div>
            <DialogFooter>
              <Button variant={'brand'} size={'responsive'} disabled={form.formState.isSubmitting}>
                {t('requests_list.change_editor', {
                  defaultValue: 'Change Editor',
                })}
                <ButtonLoader loading={form.formState.isSubmitting} />
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export { RequestEditorChangeDialog };
